import React, { useCallback, useEffect, useState } from 'react'
import { Button, Banner, TextBox, Table } from '@ds/ui'
import ExportWorkflow from '../importExport/ExportWorkflow';
import ExportInfo from '../../types/ExportInfo';
import { ImportInfo } from '../../types/ImportInfo'
import ImportWorkflow from '../importExport/ImportWorkflow';
import { KeyPairInfo } from '../../types/KeyPairInfo';
import { BannerInfo } from '../../types/BannerInfo';
import { apiFetch, handleApiError } from '../../utils/apiClient';


interface KeyPairManagementProps {
  onBack: () => void;
  keyPairInfo: KeyPairInfo | null;
  setKeyPairInfo: (info: KeyPairInfo | null) => void;
  onBannerUpdate: (bannerInfo: BannerInfo | null) => void;
}


export default function KeyPairManagement({ 
  onBack, 
  keyPairInfo, 
  setKeyPairInfo,
  onBannerUpdate
}: KeyPairManagementProps) {
  const [showCreateWorkflow, setShowCreateWorkflow] = useState(false)
  const [showImportWorkflow, setShowImportWorkflow] = useState(false)
  const [createKeyPairInfo, setCreateKeyPairInfo] = useState<ExportInfo | null>(null)
  const [importKeyPairInfo, setImportKeyPairInfo] = useState<ImportInfo | null>(null)
  const [isCreatingKeyPair, setIsCreatingKeyPair] = useState(false)

  const fetchData = useCallback(async () => {
    try {
      await handleGetCreateInfo();
      await handleGetImportInfo();
    } catch (error) {
      console.error('Error fetching keys data:', error)
    }
  }, [])

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData()
  }, [fetchData]);


  useEffect(() => {
    if (createKeyPairInfo?.inProgress === true && createKeyPairInfo?.filehash !== null) {
      setShowCreateWorkflow(true)
      setIsCreatingKeyPair(true)
    }
    if (importKeyPairInfo?.inProgress === true && importKeyPairInfo?.redKeyUploaded === true) {
      setShowImportWorkflow(true)
    } 
  }, [createKeyPairInfo, importKeyPairInfo]);

  const handleCreateComplete = () => {
    setShowCreateWorkflow(false)
    setIsCreatingKeyPair(false)
  }

  const loadPublicKey = async () => {
    try {
      const response = await fetch('Admin/Home/GetKeyPairInfo', {
        method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      });
    
      if (!response.ok) {
        await handleApiError(response);
      }
    
      const data = await response.json();
      setKeyPairInfo(data.keyPairInfo as KeyPairInfo);
      return data.keyPairInfo.publicKey;
    } catch (error) {
      console.error('Error fetching public key:', error);
      onBannerUpdate({ type: 'danger', message: 'Failed to fetch public key: ' + error });
    }
  }

  useEffect(() => {
    loadPublicKey();
  }, []);

  const handleCreateKeyPair = useCallback(async () => {
    try {
      setIsCreatingKeyPair(true);
      const response = await apiFetch('Admin/KeyPair/CreateNew', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        await handleApiError(response);
      }

      await handleGetCreateInfo();
    } catch (error) {
      console.error('Error creating key pair:', error);
      onBannerUpdate({ type: 'danger', message: 'Failed to create key pair. Please try again.' });
    } finally {
      setShowCreateWorkflow(true);
      setIsCreatingKeyPair(true);
    }
  }, []);

  const handleImportKeyPair = useCallback(async () => {
    try {
      const response = await apiFetch('Admin/KeyPair/ImportFile', {
        method: 'POST'
      })
      
      if (!response.ok) {
        await handleApiError(response);
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      await handleGetCreateInfo();
      setShowCreateWorkflow(true)
 
    } catch (error) {
      console.error('Error exporting keys:', error);
      onBannerUpdate({ type: 'danger', message: 'Failed to export keys: ' + error });
    } finally {
      setShowCreateWorkflow(false);
      setIsCreatingKeyPair(false);
    }
  }, []);

  const handleGetCreateInfo = async () => {
    try {
      const response = await apiFetch('Admin/KeyPair/GetCreateInfo', {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });
      if (!response.ok) {
        await handleApiError(response);
      }

      const data = await response.json();
      if (data.error) {
        throw new Error(data.error);
      }
      const exportInfo = data.exportInfo;
      setCreateKeyPairInfo(exportInfo);
    } catch (error) {
      console.error('Error getting create info:', error);
      onBannerUpdate({ type: 'danger', message: 'Failed to get create info: ' + error });
      throw error;
    }
  };

  const handleGetImportInfo = async () => {
    try {
      const response = await fetch('Admin/KeyPair/GetImportInfo', {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });
      if (!response.ok) {
        await handleApiError(response);
      }

      const data = await response.json();
      if (data.error) {
        throw new Error(data.error);
      }

      setImportKeyPairInfo(data as ImportInfo);
    } catch (error) {
      console.error('Error getting import info:', error);
      onBannerUpdate({ type: 'danger', message: 'Failed to get import info' });
      throw error;
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <div className="flex-1 p-6 pt-[45px]">
        <Banner kind="subtle" visible>
          <span className="text-black font-ds-indigo text-[20px] leading-[30px]">
            <strong>Appliance Public Key</strong>
            <span className="font-normal"> - Encrypting requests sent to this appliance</span>
          </span>
        </Banner>
      </div>
      <div className="flex-1 pt-[2.813rem] px-6 pb-6" style={{ paddingTop: '1.875rem' }}>
        <div className="bg-purple-50 p-4 rounded-lg mb-6">
          <Table columnSettings={[
            { alignment: 'left', width: '12.5rem' },
            { alignment: 'left' }
          ]}>
            <Table.Body>
              <Table.Row _style={{ verticalAlign: 'top'  }}>
                <Table.Cell><strong>Public Key</strong></Table.Cell>
                <Table.Cell>
                  <div style={{ 
                    wordBreak: 'break-all', 
                    maxWidth: '62.5rem'
                  }}>
                    {keyPairInfo?.publicKey || ''}
                  </div>
                </Table.Cell>
                </Table.Row>

                <Table.Row _style={{ verticalAlign: 'top'  }}>
                <Table.Cell></Table.Cell>
                <Table.Cell colSpan={4}>
                  <Button
                  kind="secondary"
                  text="Create New Key Pair"
                  onClick={handleCreateKeyPair}
                  disabled={isCreatingKeyPair || showCreateWorkflow}  
                  />

                 
                  {showCreateWorkflow && (
                    <div style={{ paddingTop: '1.875rem' }}>
                    <ExportWorkflow 
                      onBannerUpdate={onBannerUpdate}
                      mode="create"
                      controller="KeyPair"
                      onComplete={handleCreateComplete}
                    />
                    </div>
                  )}
                 
                </Table.Cell>
              </Table.Row>

              <Table.Row _style={{ verticalAlign: 'top'  }}>
                <Table.Cell></Table.Cell>
                <Table.Cell colSpan={4}>
                <Button 
                  text="Import Key Pair"
                  kind="secondary"
                  disabled={showImportWorkflow  }
                  onClick={() => {
                    setShowImportWorkflow(true);
                  }}
                />

                  <div style={{ paddingTop: '1.875rem' }}>
                  {showImportWorkflow && (
                  <ImportWorkflow
                    onBannerUpdate={onBannerUpdate}
                    mode="keyPair"
                    controller="KeyPair"
                    onComplete={async () => {
                      await loadPublicKey(); 
                      setShowImportWorkflow(false);
                    }}
                  />
                  )}
                  </div>
                </Table.Cell>
              </Table.Row>
              
            </Table.Body>
          </Table>
        </div>

      </div>
    </div>
  )
}